import { makeAutoObservable } from 'mobx';

export interface IDownloadsPanelStore {
  isVisible: boolean;
  isMinimized: boolean;
  openPanel: () => void;
  closePanel: () => void;
  minimize: () => void;
  restore: () => void;
}

const VISIBLE_KEY = 'activityPanel.isVisible';
const MINIMIZED_KEY = 'activityPanel.isMinimized';

/**
 * Reading and writing here is best-effort: a browser with storage blocked (or a
 * private window) throws on access, and an activity panel is not worth failing a
 * page render over — it simply starts closed.
 */
const readFlag = (key: string): boolean => {
  try {
    return window.localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
};

const writeFlag = (key: string, value: boolean): void => {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // ignored — the panel still behaves for this session
  }
};

/**
 * Whether the activity panel is on screen, and whether it is collapsed.
 *
 * The panel never dismisses itself: work reported there (a download, a bulk
 * assignment and its undo) outlives the moment it finished, so only the user's
 * own X closes it. Both flags survive a refresh, separately — a panel collapsed
 * out of the way comes back collapsed rather than springing open, and one left
 * open comes back open even if everything in it has since finished.
 */
export default class DownloadsPanelStore implements IDownloadsPanelStore {
  _isVisible = readFlag(VISIBLE_KEY);
  _isMinimized = readFlag(MINIMIZED_KEY);

  constructor() {
    makeAutoObservable(this);
  }

  get isVisible() {
    return this._isVisible;
  }

  get isMinimized() {
    return this._isMinimized;
  }

  /** New activity shows the panel, leaving a collapsed one collapsed. */
  openPanel() {
    this._isVisible = true;
    writeFlag(VISIBLE_KEY, true);
  }

  closePanel() {
    this._isVisible = false;
    this._isMinimized = false;
    writeFlag(VISIBLE_KEY, false);
    writeFlag(MINIMIZED_KEY, false);
  }

  minimize() {
    this._isMinimized = !this._isMinimized;
    writeFlag(MINIMIZED_KEY, this._isMinimized);
  }

  restore() {
    this._isMinimized = false;
    writeFlag(MINIMIZED_KEY, false);
  }
}
